import {
  Grid,
  Theme,
  Typography,
  createStyles,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import {
  AutoPxPriceLabel,
  Body2Text,
  CrossOut,
  DefaultCard,
  DottedHr,
  moneyFormat,
} from 'design-system';
import React, { useMemo } from 'react';

import clsx from 'clsx';

// Data Layer
import { discountTypes } from 'data-layer-cr/dist/constants';
import { deductibleCoverages } from 'data-layer-cr/dist/functions/coverages';
import { coverageForCode } from 'data-layer-cr/dist/functions/quotations';
import { CoverageCatalog, QuotationUnit } from 'data-layer-cr/dist/models';
import { GiftBoxIcon } from 'src/components/Icons';
import PricingProfileResponse from '../../../../../../models/responseTypes/PricingProfileResponse';

const { formatMoney } = moneyFormat;

type Props = {
  maxPayments: number;
  isAutoPx: boolean;
  unit: QuotationUnit;
  coverageCatalogs: CoverageCatalog[];
  collapsed: boolean;
  onCollapsedUpdate: () => void;
  currentProfile: PricingProfileResponse;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    cardContent: {
      marginTop: theme.spacing(1),
    },
    transparent: {
      backgroundColor: 'transparent',
    },
    noTopBotPadding: {
      paddingTop: '0px !important',
      paddingBottom: '0px !important',
    },
    whiteFont: {
      color: 'white',
    },
    titleFontSize: {
      fontSize: '1.188rem',
    },
    featureFontSize: {
      fontSize: '0.813rem',
      fontWeight: 500,
    },
    quoteFontSize: {
      fontSize: '1.438rem',
    },
    subtitleFontSize: {
      fontSize: '1.063rem',
    },
    pricesFontSize: {
      fontSize: '0.938rem',
      fontWeight: 700,
    },
  }),
);

const DeductibleVariantCard = ({
  maxPayments,
  isAutoPx,
  unit,
  coverageCatalogs,
  collapsed,
  onCollapsedUpdate,
  currentProfile,
}: Props): JSX.Element => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const classes = useStyles();

  const { coverages, total } = unit;
  const { grandTotal } = total;
  const usedDiscount = total.discounts || [];
  const grandTotalAutoPx =
    usedDiscount.find(
      (discount) => discount.id === discountTypes.AUTOPX_DISCOUNT_ID,
    )?.total ?? 0;
  const packageName: React.ReactNode = (
    <Grid container style={{ marginBottom: 20 }}>
      <img
        src="/svg/price_dollar.svg"
        alt="deducibles"
        style={{ marginRight: 11 }}
      />
      <Typography className={clsx(classes.whiteFont, classes.titleFontSize)}>
        {unit.package.description}
      </Typography>
    </Grid>
  );

  /**
   * The items that will be displayed inside the collapse layout
   */
  const displayItems = deductibleCoverages(coverageCatalogs).map(
    (coverage: CoverageCatalog): React.ReactNode => {
      const foundCoverage = coverageForCode(coverages, coverage.code);
      const usedPrice = foundCoverage ? foundCoverage.deductible : 0;
      const usedPercentage = foundCoverage?.deductibleRate
        ? foundCoverage.deductibleRate
        : 10;
      const description = foundCoverage
        ? `${foundCoverage?.deductibleDescription}`
        : 'min fijo por evento';
      const needPercentage = Boolean(foundCoverage?.deductibleRate);
      return (
        <React.Fragment key={coverage.code}>
          <Grid
            container
            item
            style={{ borderBottom: '1px solid white', padding: 0, margin: 12 }}
          >
            <Grid data-testid={`cov-name-${coverage.code}`} item xs={6}>
              <Typography
                className={clsx(classes.whiteFont, classes.featureFontSize)}
              >
                {coverage.name}
              </Typography>
            </Grid>
            <Grid
              data-testid={`cov-value-${coverage.code}`}
              item
              xs={6}
              container
              justifyContent="flex-end"
              className={needPercentage ? classes.noTopBotPadding : ''}
            >
              <Typography
                align="right"
                data-testid="sales-web-label-PercentageDeductibleVariant"
                className={clsx(classes.whiteFont, classes.pricesFontSize)}
              >
                {needPercentage
                  ? `${usedPercentage}%`
                  : `${formatMoney(
                      usedPrice,
                      true,
                      currentProfile.typeOfMoney,
                    )}`}
              </Typography>
            </Grid>
            {needPercentage && (
              <Grid
                container
                direction="row"
                className={classes.noTopBotPadding}
              >
                <Grid xs={2} />
                <Grid
                  data-testid="cov-min-description"
                  xs={10}
                  justifyContent="flex-end"
                >
                  <Typography
                    variant="subtitle2"
                    align="right"
                    className={classes.whiteFont}
                  >
                    {description}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Grid>
        </React.Fragment>
      );
    },
  );

  /**
   * The layout that will display the unit's totals
   */
  const totalsLayout = useMemo(() => {
    const usedPrice = isAutoPx ? grandTotalAutoPx : grandTotal;
    return (
      <Grid container justifyContent="center">
        {/* Show autoPx Price label if applicable */}
        {isAutoPx && (
          <Grid
            data-testid="autopx-label"
            item
            xs={12}
            container
            justifyContent="center"
          >
            <AutoPxPriceLabel color="secondary" variant="withAutoPx" />
          </Grid>
        )}
        {maxPayments > 1 && (
          <Grid data-testid="monthly-payments" item xs={6} container>
            <Grid item xs={12} container justifyContent="center">
              <Typography
                variant="caption"
                align="center"
                className={clsx(classes.whiteFont, classes.featureFontSize)}
              >
                <CrossOut
                  numberToCrossOut={maxPayments}
                  maxQuote={maxPayments}
                />
                Cuotas
              </Typography>
            </Grid>

            <Grid item xs={12} container justifyContent="center">
              <Typography
                variant="h2"
                align="center"
                className={clsx(classes.whiteFont, classes.quoteFontSize)}
              >
                {formatMoney(
                  usedPrice / maxPayments,
                  true,
                  currentProfile.typeOfMoney,
                )}
              </Typography>
            </Grid>

            <Grid item xs={12} container justifyContent="center">
              <Typography
                align="center"
                variant="caption"
                className={clsx(classes.whiteFont, classes.featureFontSize)}
              >
                mensual
              </Typography>
            </Grid>
          </Grid>
        )}

        <Grid data-testid="annual-payments" item xs={6} container>
          <Grid item xs={12} container justifyContent="center">
            <Typography
              variant="caption"
              align="center"
              className={clsx(classes.whiteFont, classes.featureFontSize)}
            >
              Total
            </Typography>
          </Grid>
          <Grid item xs={12} container justifyContent="center">
            <Typography
              variant="h2"
              align="center"
              className={clsx(classes.whiteFont, classes.quoteFontSize)}
            >
              {formatMoney(usedPrice, true, currentProfile.typeOfMoney)}
            </Typography>
          </Grid>
          <Grid item xs={12} container justifyContent="center">
            <Typography
              align="center"
              variant="caption"
              className={clsx(classes.whiteFont, classes.featureFontSize)}
            >
              anual
            </Typography>
          </Grid>
        </Grid>
        {/** When is new car and autoPx */}
        {maxPayments === 11 && (
          <Grid
            data-testid="free-month-label"
            item
            container
            xs={12}
            alignItems="center"
            justifyContent="center"
            style={{ marginTop: 8 }}
          >
            <GiftBoxIcon />
            <Body2Text style={{ fontSize: 13, marginLeft: 3, color: 'white' }}>
              El primer mes es GRATIS
            </Body2Text>
          </Grid>
        )}
      </Grid>
    );
  }, [
    maxPayments,
    grandTotal,
    grandTotalAutoPx,
    isAutoPx,
    currentProfile,
    classes,
  ]);

  return (
    <DefaultCard
      title={packageName}
      nonCollapsibleElement={totalsLayout}
      collapsible={isMobile}
      forceCollapsed={collapsed}
      onForceCollapsedUpdate={onCollapsedUpdate}
      background="linear-gradient(180deg, #0D2E68 0%, #123E8A 52.08%, #1E59C0 100%)"
      arrowColor="white"
    >
      <Grid
        container
        spacing={3}
        alignItems="center"
        className={classes.cardContent}
      >
        <Grid item xs={12}>
          <Typography
            className={clsx(classes.whiteFont, classes.subtitleFontSize)}
            variant="body1"
          >
            Deducibles
          </Typography>
        </Grid>
        {displayItems}
      </Grid>

      {/* Display only if AutoPx is applicable */}
      {isAutoPx && (
        <Grid container>
          <Grid item xs={12} style={{ margin: '13px 0' }}>
            <DottedHr />
          </Grid>
          <Grid item xs={12} container justifyContent="center">
            <AutoPxPriceLabel
              data-testid="without-autopx-label"
              variant="withoutAutoPx"
              basePrice={grandTotal}
            />
          </Grid>
        </Grid>
      )}
    </DefaultCard>
  );
};

export default DeductibleVariantCard;
